import { ISelectMiniOption } from '@salecycle/ui-component-library';
import { Moment } from 'moment';
import {
  DashboardCurrency,
  DashboardDateRange,
  DashboardGranularity,
  DashboardGraphParameters,
  DashboardQueryParameters,
} from '../entities';
import { getTimeAsTimestamp } from './dates';
import { TFunction } from 'react-i18next';

export function getCurrencyOptions(currencyData: DashboardCurrency[], allCurrencyLabel = ''): ISelectMiniOption[] {
  const currencyOptions = currencyData
    .filter((currencyDataPoint: DashboardCurrency) => !!currencyDataPoint)
    .map(
      (currencyDataPoint: DashboardCurrency): ISelectMiniOption => ({
        label: currencyDataPoint,
        value: currencyDataPoint,
      }),
    );

  if (!allCurrencyLabel) {
    return currencyOptions;
  }

  return [{ value: null, label: allCurrencyLabel }].concat(currencyOptions);
}

export function getGranularityForDateRange(dateRange: number, translator: TFunction<'dashboard'>) {
  if (dateRange > DashboardDateRange.LAST_THIRTY_DAYS) {
    return [
      { value: DashboardGranularity.DAY, label: translator('granularityValues.day') },
      { value: DashboardGranularity.WEEK, label: translator('granularityValues.week') },
      { value: DashboardGranularity.MONTH, label: translator('granularityValues.month') },
    ];
  } else if (dateRange > DashboardDateRange.LAST_SEVEN_DAYS) {
    return [
      { value: DashboardGranularity.HOUR, label: translator('granularityValues.hour') },
      { value: DashboardGranularity.DAY, label: translator('granularityValues.day') },
      { value: DashboardGranularity.WEEK, label: translator('granularityValues.week') },
    ];
  } else if (dateRange > DashboardDateRange.YESTERDAY) {
    return [
      { value: DashboardGranularity.FIFTEEN_MINUTES, label: translator('granularityValues.fifteenMinutes') },
      { value: DashboardGranularity.THIRTY_MINUTES, label: translator('granularityValues.thirtyMinutes') },
      { value: DashboardGranularity.HOUR, label: translator('granularityValues.hour') },
      { value: DashboardGranularity.DAY, label: translator('granularityValues.day') },
    ];
  }
  return [
    { value: DashboardGranularity.FIFTEEN_MINUTES, label: translator('granularityValues.fifteenMinutes') },
    { value: DashboardGranularity.THIRTY_MINUTES, label: translator('granularityValues.thirtyMinutes') },
    { value: DashboardGranularity.HOUR, label: translator('granularityValues.hour') },
  ];
}

export function getDefaultGranularityForDateRange(dateRange: number) {
  if (dateRange > DashboardDateRange.LAST_NINETY_DAYS) {
    return DashboardGranularity.MONTH;
  } else if (dateRange > DashboardDateRange.LAST_THIRTY_DAYS) {
    return DashboardGranularity.WEEK;
  } else if (dateRange > DashboardDateRange.LAST_SEVEN_DAYS) {
    return DashboardGranularity.DAY;
  }

  return DashboardGranularity.HOUR;
}

export function getFromToUrlSegment(queryParameters: DashboardQueryParameters, timezone: Moment): string {
  let startTime: Moment;
  let endTime: Moment;
  if (queryParameters.dateRange === 0 && queryParameters.dateRangeOffset === 0) {
    startTime = timezone.clone().startOf('day');
    endTime = timezone.clone().endOf('day');
  } else {
    startTime = timezone
      .clone()
      .subtract(queryParameters.dateRangeOffset, 'day')
      .subtract(1 * queryParameters.dateRange, 'day')
      .startOf('day');

    endTime = timezone
      .clone()
      .subtract(queryParameters.dateRangeOffset + 1, 'day')
      .endOf('day');
  }
  const from = encodeURIComponent(getTimeAsTimestamp(startTime));
  const to = encodeURIComponent(getTimeAsTimestamp(endTime));
  return `from/${from}/to/${to}`;
}

export function getComparisonFromToUrlSegment(queryParameters: DashboardQueryParameters, timezone: Moment): string {
  const startTime = timezone
    .clone()
    .subtract(queryParameters.comparisonDateRangeOffset, 'day')
    .subtract(queryParameters.comparisonDateRange, 'day')
    .startOf('day');

  const endTime = timezone
    .clone()
    .subtract(queryParameters.comparisonDateRangeOffset, 'day')
    .subtract(queryParameters.comparisonDateRange === 0 ? 0 : 1, 'day')
    .endOf('day');
  const from = encodeURIComponent(getTimeAsTimestamp(startTime));
  const to = encodeURIComponent(getTimeAsTimestamp(endTime));
  return `from/${from}/to/${to}`;
}

export function getQueryString(
  queryParameters: DashboardQueryParameters,
  graphParameters: DashboardGraphParameters,
): string {
  const params = {
    granularity: graphParameters.granularity,
    filterCurrency: queryParameters.filterCurrency,
  };
  const query = Object.keys(params)
    .map(key => (params[key] ? key + '=' + params[key] : ''))
    .filter(x => x !== '')
    .join('&');
  return `?${query}`;
}
